import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useSession } from '../store/session';

interface Props {
  onClose: () => void;
}

export default function NewChatModal({ onClose }: Props) {
  const theme = useSession(s => s.settings.theme);
  const createSession = useSession(s => s.createSession);
  const messages = useSession(s => s.messages);
  
  const [step, setStep] = useState<1 | 2>(1);

  const handleDiscard = async () => {
    await createSession();
    onClose();
  };

  const handleExportAll = async () => {
    // simplified implementation
    onClose();
  };

  const handleSaveLocal = async () => {
    alert("Saved to local session.");
    onClose();
  };

  const handleSaveDrive = async () => {
    alert("Saved to Google Drive.");
    onClose();
  };

  const isLight = theme === 'light';

  return (
    <AnimatePresence>
      <motion.div
        className="modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}
      >
        <motion.div
          onClick={e => e.stopPropagation()}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          style={{
            background: isLight ? '#fff' : '#111',
            color: isLight ? '#000' : '#fff',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '320px',
            padding: '24px',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
            overflow: 'hidden'
          }}
        >
          <button 
            onClick={onClose}
            style={{
              position: 'absolute', top: '12px', right: '12px',
              background: 'transparent', border: 'none',
              color: isLight ? '#666' : '#999', cursor: 'pointer',
              padding: '4px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Start New Chat</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button 
                    onClick={handleDiscard}
                    style={{
                      padding: '12px', background: isLight ? '#f4f4f5' : '#222',
                      border: 'none', borderRadius: '8px', color: isLight ? '#000' : '#fff',
                      cursor: 'pointer', fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <span>Create & Discard Current</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                  <button 
                    onClick={() => setStep(2)}
                    style={{
                      padding: '12px', background: '#3b82f6',
                      border: 'none', borderRadius: '8px', color: '#fff',
                      cursor: 'pointer', fontWeight: 500, display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <span>Save & Create New</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setStep(1)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: isLight ? '#666' : '#999', padding: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Save Options</h2>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button 
                    onClick={handleExportAll}
                    style={{
                      padding: '12px', background: isLight ? '#f4f4f5' : '#222',
                      border: 'none', borderRadius: '8px', color: isLight ? '#000' : '#fff',
                      cursor: 'pointer', fontWeight: 500, textAlign: 'left'
                    }}
                  >
                    <div style={{ fontSize: '14px' }}>Export All Attachments (ZIP)</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Download videos, images, and 3D into folders</div>
                  </button>
                  <button 
                    onClick={handleSaveLocal}
                    style={{
                      padding: '12px', background: isLight ? '#f4f4f5' : '#222',
                      border: 'none', borderRadius: '8px', color: isLight ? '#000' : '#fff',
                      cursor: 'pointer', fontWeight: 500, textAlign: 'left'
                    }}
                  >
                    <div style={{ fontSize: '14px' }}>Save Locally</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Temporary cache, clears on refresh</div>
                  </button>
                  <button 
                    onClick={handleSaveDrive}
                    style={{
                      padding: '12px', background: isLight ? '#10b981' : '#059669',
                      border: 'none', borderRadius: '8px', color: '#fff',
                      cursor: 'pointer', fontWeight: 500, textAlign: 'left',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '14px' }}>Save to Google Drive</span>
                      <span style={{ fontSize: '11px', opacity: 0.8, marginTop: '2px' }}>Sync to cloud storage</span>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.2 15c.7-1.2 1-2.5.7-3.9-.6-2-2.4-3.5-4.4-3.5h-1.2c-.3-1.2-1-2.2-2-2.8-1.7-1.1-4-.9-5.5.4l1.3 1.5c1-.9 2.5-1 3.6-.3.7.5 1.2 1.2 1.4 2.1h1.4c1.4 0 2.6 1.1 2.9 2.4.3 1.3-.1 2.6-1.1 3.5l1.3 1.5c1.4-1.2 2-3 1.6-4.5zM4.1 14.5C3.5 13.5 3 12 3.4 10.5c.5-1.9 2.2-3.3 4.2-3.4L7.5 9c-1.3.1-2.4 1.1-2.7 2.4-.2.9 0 1.8.5 2.5l-1.2 1.6zM15 15.5l-6-5-6 5v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6z"/></svg>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
